import React, { createContext, useContext, useState, useEffect } from 'react'

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.squadline.com'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState(localStorage.getItem('admin_token'))

  useEffect(() => {
    if (token) {
      fetchUser()
    } else {
      setLoading(false)
    }
  }, [token])

  const fetchUser = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-Environment': 'production'
        }
      })
      
      if (res.ok) {
        const data = await res.json()
        if (data.is_admin) {
          setUser(data)
        } else {
          logout()
          alert('Admin access required')
        }
      } else {
        logout()
      }
    } catch (err) {
      console.error('Auth error:', err)
      logout()
    } finally {
      setLoading(false)
    }
  }

  const login = async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'X-Environment': 'production'
      },
      body: JSON.stringify({ email, password })
    })
    
    // Parse response body once
    let data
    try {
      data = await res.json()
    } catch (e) {
      console.error('Failed to parse login response:', e)
      throw new Error('Server returned an invalid response. Please try again.')
    }
    
    if (!res.ok) {
      throw new Error(data.detail || 'Login failed')
    }
    
    // For admin panel, we need to verify admin status via another API call or check token
    // Store the access token first
    const accessToken = data.access_token
    
    // Verify the user is an admin
    const meRes = await fetch(`${API_URL}/api/auth/me`, {
      headers: { 
        'Authorization': `Bearer ${accessToken}`,
        'X-Environment': 'production'
      }
    })
    
    if (!meRes.ok) {
      throw new Error('Failed to verify user')
    }
    
    const userData = await meRes.json()
    
    if (!userData.is_admin) {
      throw new Error('Admin access required')
    }
    
    localStorage.setItem('admin_token', accessToken)
    setToken(accessToken)
    setUser(userData)
  }

  const logout = () => {
    localStorage.removeItem('admin_token')
    setToken(null)
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
